import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'training-plan');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredArticles = articles.filter((a) => a.category === activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
  };

  if (!slug) {
    return null;
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `猛男诞生记 - ${activeCategory.name}`,
    description: activeCategory.description,
    url: `https://mengnan-danshengji.com/category/${activeSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://mengnan-danshengji.com/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <SEO
        title={activeCategory.name}
        description={`猛男诞生记${activeCategory.name}专栏 - ${activeCategory.description}。共${activeCategory.count}篇原创文章，涵盖最全的猛男蜕变干货。`}
        keywords={`猛男诞生记,${activeCategory.name},猛男训练计划,硬核健身教程,猛男体型塑造`}
        canonicalUrl={`https://mengnan-danshengji.com/category/${activeSlug}`}
        schema={categorySchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: activeCategory.name },
              ]}
            />
            <div className="flex items-center gap-4 mt-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${activeCategory.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  {activeCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {activeCategory.description} · 共 {activeCategory.count} 篇文章
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-background-200 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto py-3 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="text-sm md:text-base font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-xs md:text-sm text-foreground-500 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <img src={article.authorAvatar} alt={article.author} className="w-5 h-5 rounded-full object-cover" />
                        {article.author}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span>{article.date}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span>{article.readTime} 分钟阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="mt-4 text-foreground-500">该分类暂无文章，敬请期待</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}